import { useState } from 'react';
import { TabPanel, Tabs } from '@/ui/Tabs';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { useMisSolicitudes } from './api';
import { DetalleDeSolicitud } from './DetalleDeSolicitud';
import { NuevaSolicitud } from './NuevaSolicitud';
import { FilaDeSolicitud } from './piezas';

/**
 * MisDocumentos — la vista del colaborador. `#requests`.
 *
 * **A esta pantalla no lleva ningún enlace del portal desde el 2026-08-27**: el
 * alias `#solicitudes` pasó al módulo vigente, y el ítem de menú «Formatos PDF»
 * se retiró. Se migra igual porque la entrada del `PAGE_MAP` sigue viva y un
 * marcador antiguo aterriza aquí — y lo que se encontraba era la versión con
 * las interpolaciones sin escapar.
 *
 * Las pestañas van en el hash (`#requests/mias`) por la misma razón que en el
 * resto del portal: recargar tiene que devolver a donde se estaba.
 */
const PESTANAS = [
  { clave: 'nueva', etiqueta: 'Nueva solicitud', icono: 'bi-plus-circle' },
  { clave: 'mias', etiqueta: 'Mis solicitudes', icono: 'bi-inbox' },
] as const;

const CLAVES = PESTANAS.map((p) => p.clave);

export function MisDocumentos() {
  const sub = String(window._routeParams?.[0] ?? '');
  const [pestana, setPestana] = useState(
    () => ((CLAVES as readonly string[]).includes(sub) ? sub : 'nueva'));

  return (
    <>
      <header className="mb-4">
        <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
          <i className="bi bi-file-earmark-text-fill" aria-hidden="true" /> Solicitudes de documentos
        </h1>
        <p className="mt-1 text-3 text-muted">
          Formatos que Bienestar publica; se generan con tus datos y quedan registrados como
          evidencia.
        </p>
      </header>

      <Tabs
        valor={pestana}
        onCambio={(clave) => {
          const nueva = (CLAVES as readonly string[]).includes(clave) ? clave : 'nueva';
          setPestana(nueva);
          history.replaceState(null, '', `#requests/${nueva}`);
        }}
        pestanas={[...PESTANAS]}
      >
        <TabPanel clave="nueva"><NuevaSolicitud activa={pestana === 'nueva'} /></TabPanel>
        <TabPanel clave="mias"><MisSolicitudes activa={pestana === 'mias'} /></TabPanel>
      </Tabs>
    </>
  );
}

function MisSolicitudes({ activa }: { activa: boolean }) {
  const consulta = useMisSolicitudes(activa);
  const [abierta, setAbierta] = useState<number | null>(null);
  const filas = consulta.data ?? [];

  return (
    <>
      {consulta.isPending ? (
        <SkeletonFilas filas={3} columnas={3} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="tus solicitudes"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !filas.length ? (
        <EstadoVacio
          icono="bi-inbox"
          titulo="Sin solicitudes"
          descripcion="Todavía no has enviado ninguna."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {filas.map((s) => (
            <li key={s.id}>
              <FilaDeSolicitud solicitud={s} onAbrir={() => setAbierta(s.id)} />
            </li>
          ))}
        </ul>
      )}

      {abierta != null && (
        <DetalleDeSolicitud id={abierta} esGestion={false} onCerrar={() => setAbierta(null)} />
      )}
    </>
  );
}
